import { type IAuthData } from 'matrix-js-sdk/src/interactive-auth';
import { type AuthDict, type MatrixClient, MatrixError } from 'matrix-js-sdk/src/matrix';

import { isSafari } from 'utils/browsers';
import logger from 'utils/logging/faro';

import { VerificationErrorEnum } from '../utils';

export type AuthRedirectType = 'account' | 'sso' | null;

type AuthFlow = {
  type?: string;
  stages: string[];
};

type MatrixErrorWithAuthData = MatrixError & {
  data: IAuthData;
};

// Cross-signing reset over the OAuth/MAS world (MSC4312). `m.oauth` is the stable UIA stage;
// `org.matrix.cross_signing_reset` is the legacy stage name and also the MAS account-management
// deep-link action. We accept both stages and echo the matched one back when completing the flow.
const CROSS_SIGNING_RESET_ACTION = 'org.matrix.cross_signing_reset';
const CROSS_SIGNING_RESET_STAGE = 'm.oauth';

const SSO_FLOW_TYPES = ['m.login.sso', 'org.matrix.login.sso', 'm.login.cas'];

// After the user authorises the reset in MAS, the homeserver lifts the auth requirement only
// briefly and the change can take a beat to become visible. Re-attempt completion a few times
// before giving up so a propagation lag doesn't surface as a hard failure to the user.
const RESUME_RETRY_DELAYS_MS = [0, 2_000, 4_000];

// Bootstrap or a prior reset can leave a stale /keys/query in flight; resetEncryption then never
// reaches device_signing/upload. Re-fetch device info a few times before starting the reset.
const PRE_RESET_SETTLE_DELAYS_MS = [0, 1_000, 2_000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isMatrixAuthError = (error: unknown): error is MatrixErrorWithAuthData =>
  error instanceof MatrixError &&
  error.data != null &&
  typeof error.data === 'object' &&
  'flows' in error.data &&
  'session' in error.data;

// Returned reset signals.
export const AUTH_REDIRECT_SSO = 'AUTH_REDIRECT_SSO';

// Pick the cross-signing reset stage from the advertised UIA flows, preferring the stable
// `m.oauth` stage over the legacy name. Returns undefined when no reset stage is offered.
const findCrossSigningResetStage = (flows: AuthFlow[]): string | undefined => {
  const offers = (stage: string) => flows.some((flow) => flow.type === stage || flow.stages?.includes(stage));
  if (offers(CROSS_SIGNING_RESET_STAGE)) return CROSS_SIGNING_RESET_STAGE;
  if (offers(CROSS_SIGNING_RESET_ACTION)) return CROSS_SIGNING_RESET_ACTION;
  return undefined;
};

export type ResetKeysOptions = {
  /** Tab pre-opened synchronously on click so Safari allows the later MAS navigation. */
  preOpenedWindow?: Window | null;
  /**
   * Called once the reset is paused waiting on external authorisation, before the user acts.
   * `account` pauses the in-flight reset (resume with `resume`); `sso` opens a fallback tab
   * and the reset must be re-run after the user returns.
   */
  onAuthorizationRequired?: (type: 'account' | 'sso') => void;
  /**
   * The user already authorised the cross-signing reset in MAS before this reset started (the
   * deferred-reset path: a poisoned account at the `pre_sync` login gate completes MAS first, the
   * gate is resolved so `startClient` can run, then the reset runs here with a started client).
   * In this mode the auth callback completes the UIA session directly — it does not re-open MAS or
   * wait for a Continue click, because authorisation is already done.
   */
  masAlreadyAuthorised?: boolean;
  /**
   * Skip the pre-reset `/keys/query` settle. On poisoned accounts the identity is broken
   * (`NoSignatureFound`), so the settle only adds latency and extra failing queries; skip it.
   */
  skipSettleDelays?: boolean;
};

export interface ResetEncryptionController {
  /** Resolve the MAS account-management URL for a cross-signing reset (undefined on non-MAS servers). */
  resolveAccountManagementUrl(stageParamsUrl?: string): Promise<string | undefined>;
  /** Navigate the pre-opened tab to MAS immediately within the user gesture (Safari popup-blocker). */
  openCrossSigningResetInMas(preOpenedWindow?: Window | null): Promise<boolean>;
  /** Run `crypto.resetEncryption`, driving the UIA / MAS auth callback. */
  resetKeys(options?: ResetKeysOptions): Promise<'ok' | 'auth_redirect' | 'error'>;
  /** Resume a reset paused at the MAS authorisation step. False when not yet suspended. */
  resume(): boolean;
}

/**
 * Builds a controller that owns the cross-signing reset + MAS/UIA authorisation flow with no React
 * state of its own. The interactive caller (`useResetKeys` → `ResetKeysModal`) wires
 * `onAuthRedirectTypeChange` to component state and drives Continue via `resume`. The non-interactive
 * caller (`useStartMatrixClient`, deferred reset after `startClient`) runs `resetKeys` with
 * `masAlreadyAuthorised` so completion happens without UI.
 */
export const createResetEncryptionController = (
  matrixClient: MatrixClient,
  onAuthRedirectTypeChange?: (type: AuthRedirectType) => void
): ResetEncryptionController => {
  // Resolver for the suspended reset. `resetEncryption` keeps running while the user authorises in
  // MAS; the Continue click calls `resume` to resolve this and finish the same UIA session instead
  // of restarting the whole reset (which would rotate keys again and stall poisoned accounts).
  let resumeResolver: (() => void) | null = null;

  const setAuthRedirectType = (type: AuthRedirectType): void => onAuthRedirectTypeChange?.(type);

  const resolveAccountManagementUrl = async (stageParamsUrl?: string): Promise<string | undefined> => {
    if (stageParamsUrl) return stageParamsUrl;

    try {
      const authMetadata = await matrixClient.getAuthMetadata();
      if (authMetadata?.account_management_uri) {
        const url = new URL(authMetadata.account_management_uri);
        url.searchParams.set('action', CROSS_SIGNING_RESET_ACTION);
        return url.toString();
      }
    } catch (metadataError) {
      logger.error('Failed to retrieve account management URL', metadataError);
    }

    return undefined;
  };

  const openAuthRedirect = (redirectUrl: string, preOpenedWindow?: Window | null): void => {
    if (preOpenedWindow && !preOpenedWindow.closed) {
      preOpenedWindow.location.href = redirectUrl;
      return;
    }
    if (isSafari) {
      window.location.assign(redirectUrl);
      return;
    }
    window.open(redirectUrl, '_blank');
  };

  const openCrossSigningResetInMas = async (preOpenedWindow?: Window | null): Promise<boolean> => {
    const accountUrl = await resolveAccountManagementUrl();
    if (!accountUrl) return false;
    openAuthRedirect(accountUrl, preOpenedWindow);
    logger.info('Reset keys: MAS opened for cross-signing reset');
    return true;
  };

  const processAuthentication = async (
    makeRequest: (authData: AuthDict | null) => Promise<void>,
    options?: ResetKeysOptions
  ): Promise<void> => {
    const { preOpenedWindow, onAuthorizationRequired, masAlreadyAuthorised } = options ?? {};

    // Send the user to MAS, then keep the in-flight reset paused until they return and press
    // Continue. On resume we finish the SAME upload with `completionAuth` rather than starting
    // over, so the keys MAS approved are the keys we publish. When `masAlreadyAuthorised` is set
    // the user already approved in MAS, so we complete the upload directly without UI.
    const authoriseInMasThenComplete = async (accountUrl: string, completionAuth: AuthDict | null): Promise<void> => {
      if (!masAlreadyAuthorised) {
        // Arm the resolver before announcing the pause so a Continue click can never race ahead
        // of the suspension point.
        const userAuthorised = new Promise<void>((resolve) => {
          resumeResolver = resolve;
        });

        openAuthRedirect(accountUrl, preOpenedWindow);
        setAuthRedirectType('account');
        onAuthorizationRequired?.('account');

        await userAuthorised;
        resumeResolver = null;
      }

      let lastError: unknown;
      for (let attempt = 0; attempt < RESUME_RETRY_DELAYS_MS.length; attempt++) {
        if (RESUME_RETRY_DELAYS_MS[attempt] > 0) await sleep(RESUME_RETRY_DELAYS_MS[attempt]);
        try {
          await makeRequest(completionAuth);
          setAuthRedirectType(null);
          return;
        } catch (resumeError) {
          lastError = resumeError;
          const masNotVisibleYet =
            isMatrixAuthError(resumeError) || (resumeError instanceof MatrixError && resumeError.httpStatus === 401);
          if (masNotVisibleYet && attempt < RESUME_RETRY_DELAYS_MS.length - 1) {
            logger.info('Reset keys: cross-signing reset not visible to homeserver yet, retrying', {
              attempt: attempt + 1,
            });
            continue;
          }
          throw resumeError;
        }
      }

      throw lastError instanceof Error ? lastError : new Error(VerificationErrorEnum.UNKNOWN_ERROR);
    };

    try {
      await makeRequest(null);
      setAuthRedirectType(null);
      return;
    } catch (error) {
      // Poisoned accounts on MAS-fronted servers answer device_signing/upload with a 401 that
      // carries no UIA flows. There is no session to complete, so we authorise in MAS and retry
      // the bare upload (no auth) once the server has lifted the requirement.
      if (!isMatrixAuthError(error)) {
        if (error instanceof MatrixError && error.httpStatus === 401) {
          const accountUrl = await resolveAccountManagementUrl();
          if (accountUrl) {
            logger.info('Reset keys: redirecting to MAS after non-UIA 401');
            await authoriseInMasThenComplete(accountUrl, null);
            return;
          }
          logger.warn('Reset keys: 401 with no account management URL available');
        }
        throw error;
      }

      const availableFlows = (error.data.flows || []) as AuthFlow[];
      const sessionId = error.data.session;
      const resetStage = findCrossSigningResetStage(availableFlows);

      // Cross-signing reset takes priority over any password stage Synapse advertises alongside
      // it — we have no password UI here and must complete the reset through MAS.
      if (resetStage && sessionId) {
        const accountUrl = await resolveAccountManagementUrl(error.data.stageParams?.url);
        if (!accountUrl) throw new Error('Authentication required but no account management URL available');
        await authoriseInMasThenComplete(accountUrl, { type: resetStage, session: sessionId });
        return;
      }

      const ssoFlow = availableFlows.find((flow) => flow.type && SSO_FLOW_TYPES.includes(flow.type));
      if (ssoFlow?.type && sessionId) {
        // The deferred path has no UI to drive an SSO fallback round-trip; surface as an error so
        // the caller can keep the user moving (they can retry the reset from settings).
        if (masAlreadyAuthorised) {
          throw new Error('Authentication required but no supported flow available');
        }
        const ssoAuthType = ['org.matrix.login.sso', 'm.login.sso'].includes(ssoFlow.type) ? 'sso' : 'cas';
        const ssoAuthUrl = matrixClient.getFallbackAuthUrl(ssoAuthType, sessionId);
        openAuthRedirect(ssoAuthUrl, preOpenedWindow);
        setAuthRedirectType('sso');
        onAuthorizationRequired?.('sso');
        throw new Error(AUTH_REDIRECT_SSO);
      }

      throw new Error('Authentication required but no supported flow available');
    }
  };

  const resume = (): boolean => {
    if (!resumeResolver) {
      logger.info('Reset keys: resume requested before reset reached auth suspension');
      return false;
    }
    resumeResolver();
    return true;
  };

  const resetKeys = async (options?: ResetKeysOptions): Promise<'ok' | 'auth_redirect' | 'error'> => {
    const crypto = matrixClient.getCrypto();
    if (!crypto) return 'error';

    setAuthRedirectType(null);

    const userId = matrixClient.getUserId();
    if (userId && !options?.skipSettleDelays) {
      // Let any in-flight `/keys/query` from bootstrap or a prior reset settle before
      // resetEncryption touches cross-signing identity — otherwise the SDK can stall and
      // never reach the auth callback (Faro ZHwe8koAyf: second reset hung here).
      for (let i = 0; i < PRE_RESET_SETTLE_DELAYS_MS.length; i++) {
        if (PRE_RESET_SETTLE_DELAYS_MS[i] > 0) await sleep(PRE_RESET_SETTLE_DELAYS_MS[i]);
        await crypto.getUserDeviceInfo([userId], true).catch(() => null);
      }
    }

    try {
      await crypto.resetEncryption((makeRequest) => processAuthentication(makeRequest, options));
      return 'ok';
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : VerificationErrorEnum.UNKNOWN_ERROR;

      if (errorMessage === AUTH_REDIRECT_SSO) {
        return 'auth_redirect';
      }

      setAuthRedirectType(null);
      logger.error('Failed to reset encryption keys', err);
      return 'error';
    }
  };

  return { resolveAccountManagementUrl, openCrossSigningResetInMas, resetKeys, resume };
};
